// TanStack Query hooks for object_type_mappings CRUD.
// Includes header-signature lookup for auto-apply.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Mapping } from "@/types/ontology";

// ── Query keys (plan §1.3) ────────────────────────────────────────────────────

export const MAPPINGS_KEY = ["mappings"] as const;
export const MAPPINGS_BY_SIG_KEY = ["mappings-by-sig"] as const;

// ── Query ─────────────────────────────────────────────────────────────────────

/** List all non-deleted mappings for a given object type. */
export function useMappings(objectTypeId: string | null) {
  const query = useQuery({
    queryKey: [...MAPPINGS_KEY, objectTypeId],
    enabled: objectTypeId !== null,
    queryFn: async () => {
      if (!objectTypeId) return [];

      const { data, error } = await supabase
        .from("object_type_mappings")
        .select("*")
        .eq("object_type_id", objectTypeId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);

      return (data as Mapping[]) ?? [];
    },
  });

  return { mappings: query.data ?? [], isLoading: query.isLoading, error: query.error };
}

// ── Query: list all non-deleted mappings for a header signature ───────────────

/**
 * Fetch all non-deleted mappings that match a given header_signature,
 * regardless of which object type they target.
 * Used in DataSourcesPage to show which Object Types a data source maps to,
 * and to avoid offering to re-create the same mapping.
 */
export function useMappingsBySignature(headerSignature: string | null) {
  const query = useQuery({
    queryKey: [...MAPPINGS_BY_SIG_KEY, headerSignature],
    enabled: headerSignature !== null && headerSignature !== "",
    queryFn: async () => {
      if (!headerSignature) return [];

      const { data, error } = await supabase
        .from("object_type_mappings")
        .select("*")
        .eq("header_signature", headerSignature)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);

      return (data as Mapping[]) ?? [];
    },
  });

  return { mappings: query.data ?? [], isLoading: query.isLoading, error: query.error };
}

// ── Query: list all non-deleted mappings for a data source (by data_source_id) ─

/**
 * Fetch all non-deleted mappings that apply to a specific data source.
 * Because object_type_mappings has no data_source_id column, mappings are
 * associated with a data source via their header_signature (derived from the
 * CSV column set). Two data sources with identical column headers share the
 * same signature and therefore the same pool of applicable mappings.
 *
 * This is intentional: a mapping template describes how to map *a schema*
 * (identified by its header signature) to an Object Type, not a specific file.
 * Surfacing all signature-matching mappings for a data source is correct behaviour.
 */
export function useMappingsByDataSourceId(
  dataSourceId: string | null,
  headerSignature: string | null
) {
  const query = useQuery({
    queryKey: [...MAPPINGS_BY_SIG_KEY, "ds", dataSourceId, headerSignature],
    enabled: dataSourceId !== null && headerSignature !== null && headerSignature !== "",
    queryFn: async () => {
      if (!headerSignature) return [];

      const { data, error } = await supabase
        .from("object_type_mappings")
        .select("*")
        .eq("header_signature", headerSignature)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw new Error(error.message);

      return (data as Mapping[]) ?? [];
    },
  });

  return { mappings: query.data ?? [], isLoading: query.isLoading, error: query.error };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Find the first non-deleted mapping for an object type that matches the given
 * header signature. Returns null if no match.
 * Used by the upload wizard for auto-apply.
 */
export function useFindMappingBySignature() {
  return async function findMappingBySignature(
    objectTypeId: string,
    headerSignature: string
  ): Promise<Mapping | null> {
    const { data, error } = await supabase
      .from("object_type_mappings")
      .select("*")
      .eq("object_type_id", objectTypeId)
      .eq("header_signature", headerSignature)
      .is("deleted_at", null)
      .limit(1)
      .maybeSingle();

    if (error) throw new Error(error.message);

    return (data as Mapping | null) ?? null;
  };
}

// ── Mutations ─────────────────────────────────────────────────────────────────

interface SaveMappingArgs {
  id?: string;
  objectTypeId: string;
  name: string;
  primaryKeyColumn: string;
  columnMap: Record<string, string>;
  headerSignature: string;
}

export function useSaveMapping() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      objectTypeId,
      name,
      primaryKeyColumn,
      columnMap,
      headerSignature,
    }: SaveMappingArgs) => {
      const payload = {
        object_type_id: objectTypeId,
        name,
        primary_key_column: primaryKeyColumn,
        column_map: columnMap as unknown as Record<string, unknown>,
        header_signature: headerSignature,
      };

      if (id) {
        const { data, error } = await supabase
          .from("object_type_mappings")
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq("id", id)
          .select()
          .single();

        if (error) throw new Error(error.message);

        return data as Mapping;
      } else {
        const { data, error } = await supabase
          .from("object_type_mappings")
          .insert(payload)
          .select()
          .single();

        if (error) throw new Error(error.message);

        return data as Mapping;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...MAPPINGS_KEY, variables.objectTypeId] });
      queryClient.invalidateQueries({ queryKey: MAPPINGS_KEY });
    },
    onError: (err) => {
      console.error("hook:useSaveMapping", err);
      toast({
        title: "Failed to save mapping",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}

export function useSoftDeleteMapping() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, objectTypeId }: { id: string; objectTypeId: string }) => {
      const { error } = await supabase
        .from("object_type_mappings")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id)
        .is("deleted_at", null);

      if (error) throw new Error(error.message);

      return objectTypeId;
    },
    onSuccess: (objectTypeId) => {
      queryClient.invalidateQueries({ queryKey: [...MAPPINGS_KEY, objectTypeId] });
      queryClient.invalidateQueries({ queryKey: MAPPINGS_KEY });
    },
    onError: (err) => {
      console.error("hook:useSoftDeleteMapping", err);
      toast({
        title: "Failed to delete mapping",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}
